import { useEffect, useState, useCallback, useRef } from "react";
import { useNavigate, Link } from "react-router-dom";
import { api, fmtMoney, STAGE_COLOR } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Plus, Search, Trash2, X, MapPin, Phone, User, FileText, Download, Upload, Image as ImageIcon, ChevronUp, ChevronDown, Users2, AlertTriangle, UserSearch } from "lucide-react";
import { LastContactBadge } from "@/components/leads/LastContactBadge";
import { RehabBadge } from "@/components/RehabScopeCard";
import { matchesFollowUp } from "@/lib/followUp";
import { downloadCsv } from "@/lib/csv";
import { toast } from "sonner";
import ImportLeadsModal from "@/components/ImportLeadsModal";
import BuyBoxPreviewPanel from "@/components/BuyBoxPreviewPanel";
import { AddressAutocomplete } from "@/components/AddressAutocomplete";
import CallButton from "@/components/CallButton";
import SkipTracePanel from "@/components/SkipTracePanel";
import StreetViewThumb from "@/components/StreetViewThumb";

const STAGES = ["New Lead", "Contacted", "Analyzed", "Hot — Buyer Interested", "Offer Sent", "Under Contract", "Closed", "Dead"];
const SOURCES = ["Direct Mail", "PPC", "Driving for Dollars", "Referral", "Cold Call", "SMS", "Other"];

const EMPTY = {
  property_address: "", city: "", state: "", zip_code: "",
  bedrooms: "", bathrooms: "", sqft: "",
  asking_price: "", estimated_arv: "", estimated_repairs: "",
  seller_name: "", seller_phone: "", seller_email: "",
  lead_source: "Other", notes: "", stage: "New Lead",
  photos: [],
};

export default function Leads() {
  const { user } = useAuth();
  const isPaid = (user?.tier && user.tier !== "free") || user?.role === "admin";
  const [leads, setLeads] = useState([]);
  const [open, setOpen] = useState(false);
  const [filter, setFilter] = useState("");
  const [stageFilter, setStageFilter] = useState("All");
  const [followUpFilter, setFollowUpFilter] = useState("all");
  // List-stacking filters
  const [listFilter, setListFilter] = useState("");        // exact list-source name
  const [stackFilter, setStackFilter] = useState("all");   // "all" | "1" | "2" | "3plus"
  const [listSummary, setListSummary] = useState(null);    // { sources, stack_buckets }
  const [editing, setEditing] = useState(null); // lead obj | null
  const [importOpen, setImportOpen] = useState(false);
  const nav = useNavigate();

  const fetchLeads = useCallback(async () => {
    const { data } = await api.get("/leads");
    setLeads(data);
  }, []);

  useEffect(() => { fetchLeads(); }, [fetchLeads]);

  // List-stacking summary (Pro+). Fail silently for Starter (402) and just
  // hide the pyramid card.
  useEffect(() => {
    if (!isPaid) { setListSummary(null); return; }
    let alive = true;
    api.get("/leads/lists")
      .then((r) => { if (alive) setListSummary(r.data); })
      .catch(() => { if (alive) setListSummary(null); });
    return () => { alive = false; };
  }, [isPaid, leads.length]);

  const onCreate = () => { setEditing(null); setOpen(true); };
  const onEdit = (lead) => { setEditing(lead); setOpen(true); };

  const onDelete = async (id) => {
    if (!window.confirm("Move this lead to Trash? You can restore it within 30 days.")) return;
    await api.delete(`/leads/${id}`);
    fetchLeads();
  };

  const [shareTarget, setShareTarget] = useState(null);  // lead being shared
  const [skipTraceLead, setSkipTraceLead] = useState(null);  // lead being traced

  const onShareToCommunity = (lead) => setShareTarget(lead);
  const onSkipTrace = (lead) => setSkipTraceLead(lead);

  const onExport = () => {
    if (visible.length === 0) {
      toast.error("Nothing to export — your filter shows zero leads.");
      return;
    }
    const columns = [
      { label: "property_address", accessor: (r) => r.property_address || "" },
      { label: "city", accessor: (r) => r.city || "" },
      { label: "state", accessor: (r) => r.state || "" },
      { label: "zip_code", accessor: (r) => r.zip_code || "" },
      { label: "bedrooms", accessor: (r) => r.bedrooms ?? "" },
      { label: "bathrooms", accessor: (r) => r.bathrooms ?? "" },
      { label: "sqft", accessor: (r) => r.sqft ?? "" },
      { label: "asking_price", accessor: (r) => r.asking_price ?? "" },
      { label: "estimated_arv", accessor: (r) => r.estimated_arv ?? "" },
      { label: "estimated_repairs", accessor: (r) => r.estimated_repairs ?? "" },
      { label: "seller_name", accessor: (r) => r.seller_name || "" },
      { label: "seller_phone", accessor: (r) => r.seller_phone || "" },
      { label: "seller_email", accessor: (r) => r.seller_email || "" },
      { label: "lead_source", accessor: (r) => r.lead_source || "" },
      { label: "stage", accessor: (r) => r.stage || "" },
      { label: "ai_verdict", accessor: (r) => r.ai_verdict || "" },
      { label: "notes", accessor: (r) => r.notes || "" },
      { label: "created_at", accessor: (r) => r.created_at || "" },
      { label: "updated_at", accessor: (r) => r.updated_at || "" },
    ];
    const stamp = new Date().toISOString().slice(0, 10);
    const suffix = stageFilter !== "All" ? `-${stageFilter.toLowerCase().replace(/\s+/g, "-")}` : "";
    downloadCsv(`ispeedtoclose-leads${suffix}-${stamp}.csv`, columns, visible);
    toast.success(`Exported ${visible.length} lead${visible.length === 1 ? "" : "s"}`);
  };

  const visible = leads
    .filter((l) => stageFilter === "All" || l.stage === stageFilter)
    .filter((l) => matchesFollowUp(l, followUpFilter))
    .filter((l) => {
      if (stackFilter === "all") return true;
      const lc = l.list_count || 0;
      if (stackFilter === "1") return lc <= 1;
      if (stackFilter === "2") return lc === 2;
      if (stackFilter === "3plus") return lc >= 3;
      return true;
    })
    .filter((l) => {
      if (!listFilter) return true;
      return (l.list_sources || []).includes(listFilter);
    })
    .filter((l) => {
      if (!filter) return true;
      const q = filter.toLowerCase();
      return (
        (l.property_address || "").toLowerCase().includes(q) ||
        (l.city || "").toLowerCase().includes(q) ||
        (l.seller_name || "").toLowerCase().includes(q)
      );
    });

  return (
    <div data-testid="leads-page">
      <div className="topbar">
        <div>
          <div className="crumb">Leads</div>
          <h1 className="font-display font-extrabold text-3xl tracking-tight mt-1">Lead CRM</h1>
        </div>
        <div className="flex items-center gap-2">
          <Link
            to="/app/leads/trash"
            className="btn btn-ghost"
            data-testid="leads-trash-link"
            title="View deleted leads (restorable within 30 days)"
          >
            <Trash2 size={14} /> Trash
          </Link>
          <button
            type="button"
            className="btn btn-ghost"
            onClick={() => setImportOpen(true)}
            data-testid="leads-import-button"
          >
            <Upload size={14} /> Import
          </button>
          <button
            type="button"
            className="btn btn-ghost"
            onClick={onExport}
            data-testid="leads-export-csv"
          >
            <Download size={14} /> Export
          </button>
          <button type="button" className="btn-money" onClick={onCreate} data-testid="leads-add-button">
            <Plus size={16} /> Add Lead
          </button>
        </div>
      </div>

      {listSummary && (listSummary.stack_buckets?.length > 0 || listSummary.sources?.length > 0) && (
        <StackPyramidCard
          summary={listSummary}
          stackFilter={stackFilter}
          setStackFilter={setStackFilter}
          listFilter={listFilter}
          setListFilter={setListFilter}
        />
      )}

      <div className="card mb-5 flex flex-wrap items-center gap-3">
        <div className="relative flex-1 min-w-[240px]">
          <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-[var(--ink-3)]" />
          <input
            className="input pl-9"
            placeholder="Search address, city, or seller…"
            value={filter}
            onChange={(e) => setFilter(e.target.value)}
            data-testid="leads-search-input"
          />
        </div>
        <select className="select max-w-[200px]" value={stageFilter} onChange={(e) => setStageFilter(e.target.value)} data-testid="leads-stage-filter">
          <option>All</option>
          {STAGES.map((s) => <option key={s}>{s}</option>)}
        </select>
        <select
          className="select max-w-[200px]"
          value={followUpFilter}
          onChange={(e) => setFollowUpFilter(e.target.value)}
          data-testid="leads-followup-filter"
          title="Filter by follow-up urgency"
        >
          <option value="all">Any follow-up</option>
          <option value="needs">Needs follow-up</option>
          <option value="stale">Stale 7+ days</option>
          <option value="never">Never called</option>
        </select>
        <div className="text-sm text-[var(--ink-2)] ml-auto font-mono tabular-nums">{visible.length} of {leads.length}</div>
      </div>

      {visible.length === 0 ? (
        <div className="empty" data-testid="leads-empty">
          <User size={28} className="mx-auto mb-3 text-[var(--ink-3)]" />
          <div className="font-semibold mb-1">
            {leads.length === 0 ? "You haven't added any leads yet" : "No leads match your filters"}
          </div>
          <div className="text-sm mb-4 text-[var(--ink-2)]">
            {leads.length === 0
              ? "Add your first property to run an AI analysis, generate a branded PDF, and start working the deal."
              : "Try clearing filters or adding your first lead."}
          </div>
          <button
            type="button"
            className="btn-money"
            onClick={onCreate}
            data-testid="leads-empty-add-button"
          >
            <Plus size={16} /> Add Your First Lead
          </button>
        </div>
      ) : (
        <div className="card-flat overflow-x-auto">
          <table className="tbl min-w-[860px]" data-testid="leads-table">
            <thead>
              <tr>
                <th>Property</th>
                <th>Seller</th>
                <th>Source</th>
                <th>Stage</th>
                <th className="text-right">Asking</th>
                <th className="text-right">ARV</th>
                <th></th>
              </tr>
            </thead>
            <tbody>
              {visible.map((l) => (
                <tr key={l.id} onClick={() => onEdit(l)} className="cursor-pointer" data-testid={`lead-row-${l.id}`}>
                  <td>
                    <div className="flex items-center gap-3">
                      {l.photos && l.photos.length > 0 ? (
                        <div className="relative flex-shrink-0">
                          <img
                            src={l.photos[0]}
                            alt=""
                            className="w-12 h-12 rounded-md object-cover border border-[var(--line)]"
                            onError={(e) => { e.target.style.display = "none"; }}
                            data-testid={`lead-photo-thumb-${l.id}`}
                          />
                          {l.photos.length > 1 && (
                            <span
                              className="absolute -bottom-1 -right-1 bg-[var(--ink)] text-white text-[9px] font-bold rounded-full w-4 h-4 flex items-center justify-center"
                              title={`${l.photos.length} photos`}
                            >
                              {l.photos.length}
                            </span>
                          )}
                        </div>
                      ) : (
                        <div className="w-12 h-12 rounded-md overflow-hidden border border-[var(--line)] flex-shrink-0">
                          <StreetViewThumb
                            leadId={l.id}
                            alt=""
                            className="w-12 h-12"
                          />
                        </div>
                      )}
                      <div className="min-w-0">
                        <div className="font-semibold truncate">{l.property_address}</div>
                        <div className="text-xs text-[var(--ink-2)] mt-0.5 truncate">{[l.city, l.state, l.zip_code].filter(Boolean).join(", ")}</div>
                        {l.rehab_estimate?.range_mid && (
                          <div className="mt-1.5">
                            <RehabBadge estimate={l.rehab_estimate} compact />
                          </div>
                        )}
                      </div>
                    </div>
                  </td>
                  <td>
                    <div className="text-sm">{l.seller_name || "—"}</div>
                    {l.seller_phone && (
                      <div className="text-xs text-[var(--ink-2)] flex items-center gap-1 mt-0.5">
                        <Phone size={11} /> {l.seller_phone}
                        <span onClick={(e) => e.stopPropagation()}>
                          <CallButton phone={l.seller_phone} variant="icon" />
                        </span>
                      </div>
                    )}
                    <LastContactBadge lastCall={l.last_call} leadId={l.id} />
                  </td>
                  <td className="text-sm">{l.lead_source}</td>
                  <td>
                    <span className="chip" style={{ background: STAGE_COLOR[l.stage] + "20", borderColor: STAGE_COLOR[l.stage] + "55", color: STAGE_COLOR[l.stage] }}>
                      <span className="chip-dot" style={{ background: STAGE_COLOR[l.stage] }} /> {l.stage}
                    </span>
                  </td>
                  <td className="text-right font-mono tabular-nums">{fmtMoney(l.asking_price)}</td>
                  <td className="text-right font-mono tabular-nums">{fmtMoney(l.estimated_arv)}</td>
                  <td className="text-right">
                    <div className="flex items-center justify-end gap-1">
                      <button
                        type="button"
                        onClick={(e) => { e.stopPropagation(); nav(`/v3/properties/${l.id}`); }}
                        className="btn-ghost"
                        title="Open Property Command Center"
                        data-testid={`lead-report-${l.id}`}
                      >
                        <FileText size={14} />
                      </button>
                      {isPaid && (
                        <button
                          type="button"
                          onClick={(e) => { e.stopPropagation(); onSkipTrace(l); }}
                          className="btn-ghost"
                          title="Skip trace owner contact info"
                          data-testid={`lead-skip-trace-${l.id}`}
                        >
                          <UserSearch size={14} />
                        </button>
                      )}
                      {isPaid && (
                        <button
                          type="button"
                          onClick={(e) => { e.stopPropagation(); onShareToCommunity(l); }}
                          className="btn-ghost"
                          title="Share to Community Wall"
                          data-testid={`lead-share-${l.id}`}
                        >
                          <Users2 size={14} />
                        </button>
                      )}
                      <button
                        type="button"
                        onClick={(e) => { e.stopPropagation(); onDelete(l.id); }}
                        className="btn-ghost"
                        data-testid={`lead-delete-${l.id}`}
                      >
                        <Trash2 size={14} />
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {open && (
        <LeadModal
          initial={editing}
          onClose={() => setOpen(false)}
          onSaved={() => { setOpen(false); fetchLeads(); }}
        />
      )}

      {importOpen && (
        <ImportLeadsModal
          onClose={() => setImportOpen(false)}
          onComplete={() => { fetchLeads(); }}
        />
      )}

      {shareTarget && (
        <ShareToCommunityModal
          lead={shareTarget}
          onClose={() => setShareTarget(null)}
        />
      )}

      {skipTraceLead && (
        <SkipTracePanel
          leadId={skipTraceLead.id}
          leadAddress={skipTraceLead.property_address}
          onClose={() => setSkipTraceLead(null)}
          onPhonePromoted={() => { setSkipTraceLead(null); fetchLeads(); }}
        />
      )}
    </div>
  );
}

function ShareToCommunityModal({ lead, onClose }) {
  const [fee, setFee] = useState(lead.asking_assignment_fee || "");
  const [notes, setNotes] = useState("");
  const [busy, setBusy] = useState(false);
  const nav = useNavigate();

  const submit = async () => {
    setBusy(true);
    try {
      const { data } = await api.post("/community/listings", {
        lead_id: lead.id,
        asking_assignment_fee: fee ? Number(fee) : null,
        share_notes: notes.trim() || null,
      });
      toast.success("Listing published to the Community wall");
      onClose();
      nav("/app/community");
      return data;
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Couldn't publish listing");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/40 backdrop-blur-sm flex items-center justify-center p-4" onClick={onClose}>
      <div className="bg-white rounded-xl shadow-xl max-w-md w-full p-6" onClick={(e) => e.stopPropagation()} data-testid="share-community-modal">
        <h2 className="font-display font-extrabold text-xl mb-1 flex items-center gap-2">
          <Users2 size={18} className="text-emerald-600" /> Share to Community
        </h2>
        <p className="text-sm text-[var(--ink-2)] mb-5">
          Publish a masked version of <span className="font-semibold">{lead.property_address}</span>.
          Other wholesalers see your asking fee and basic stats — but the street address stays blurred until you accept their interest.
        </p>

        <div className="space-y-3">
          <div>
            <label className="text-xs uppercase tracking-wider font-semibold text-[var(--ink-2)]">Asking Assignment Fee (USD)</label>
            <input
              type="number"
              className="mt-1 w-full px-3 py-2 rounded-md border border-[var(--line)] focus:outline-none focus:border-emerald-500"
              placeholder="10000"
              value={fee}
              onChange={(e) => setFee(e.target.value)}
              data-testid="share-fee"
            />
          </div>
          <div>
            <label className="text-xs uppercase tracking-wider font-semibold text-[var(--ink-2)]">Notes for buyers (optional)</label>
            <textarea
              rows={3}
              maxLength={2000}
              className="mt-1 w-full px-3 py-2 rounded-md border border-[var(--line)] focus:outline-none focus:border-emerald-500 text-sm"
              placeholder="e.g. Vacant. Cash only. Roof 6 months. Need to close by 3/15."
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              data-testid="share-notes"
            />
            <div className="text-[10px] text-[var(--ink-2)] mt-1 flex items-center justify-between">
              <span className="italic">Phone numbers, emails, and links are auto-redacted to keep buyers on-platform.</span>
              <span>{notes.length}/2000</span>
            </div>
          </div>
        </div>

        <div className="flex items-center justify-end gap-2 mt-6">
          <button type="button" className="btn-ghost" onClick={onClose} disabled={busy}>Cancel</button>
          <button
            type="button"
            className="btn-primary inline-flex items-center gap-1.5"
            disabled={busy}
            onClick={submit}
            data-testid="share-submit"
          >
            {busy ? "Publishing…" : "Publish to wall"}
          </button>
        </div>
      </div>
    </div>
  );
}

function LeadModal({ initial, onClose, onSaved }) {
  const [form, setForm] = useState(() => {
    if (!initial) return EMPTY;
    return { ...EMPTY, ...initial, photos: initial.photos || [] };
  });
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");
  const [photoDraft, setPhotoDraft] = useState("");
  // ── Smart duplicate detection (create mode only) ─────────────────────────
  const isEdit = !!initial;
  const [dupes, setDupes] = useState([]);
  const [dupeChecking, setDupeChecking] = useState(false);
  const [dupeDismissed, setDupeDismissed] = useState(false);
  const [mergingId, setMergingId] = useState(null);
  const dupeTimer = useRef(null);
  const nav = useNavigate();

  // Merge the in-progress form into one of the suggested dupes. Backend
  // applies fill-only rules on contact/comps fields, appends notes with a
  // timestamp header, and unions photos. Address + stage are preserved.
  const handleMerge = async (existingId) => {
    setMergingId(existingId);
    try {
      const { data } = await api.post(`/leads/${existingId}/merge`, form);
      onClose();
      nav(`/v3/properties/${data.id}`);
    } catch (e) {
      // Pydantic 422s come back as detail:[{msg, loc, …}] — flatten so the
      // user sees something useful instead of "[object Object]".
      const detail = e?.response?.data?.detail;
      const msg = Array.isArray(detail)
        ? detail.map((d) => d.msg || JSON.stringify(d)).join("; ")
        : (typeof detail === "string" ? detail : "Couldn't merge — try opening the lead instead.");
      setError(msg);
      setMergingId(null);
    }
  };

  useEffect(() => {
    if (isEdit) return;  // don't dupe-check when editing an existing lead
    if (dupeDismissed) return;
    const addr = (form.property_address || "").trim();
    if (addr.length < 4) return;
    if (dupeTimer.current) clearTimeout(dupeTimer.current);
    dupeTimer.current = setTimeout(async () => {
      setDupeChecking(true);
      try {
        const { data } = await api.post("/leads/check-duplicate", {
          property_address: addr,
          city: form.city || null,
          state: form.state || null,
          zip_code: form.zip_code || null,
        });
        setDupes(data?.matches || []);
      } catch {
        setDupes([]);
      } finally {
        setDupeChecking(false);
      }
    }, 450);
    return () => { if (dupeTimer.current) clearTimeout(dupeTimer.current); };
  }, [form.property_address, form.city, form.state, form.zip_code, isEdit, dupeDismissed]);

  // When the address is cleared/too short, hide any stale dupe warnings.
  const showDupes = !isEdit && !dupeDismissed && (form.property_address || "").trim().length >= 4 && dupes.length > 0;

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const addPhoto = () => {
    const url = photoDraft.trim();
    if (!url) return;
    if (!/^https?:\/\//i.test(url)) {
      toast.error("Photo URL must start with http:// or https://");
      return;
    }
    if (form.photos.includes(url)) {
      toast.error("That URL is already in the gallery");
      return;
    }
    if (form.photos.length >= 12) {
      toast.error("Maximum 12 photos per lead");
      return;
    }
    setForm((f) => ({ ...f, photos: [...f.photos, url] }));
    setPhotoDraft("");
  };

  const removePhoto = (idx) => {
    setForm((f) => ({ ...f, photos: f.photos.filter((_, i) => i !== idx) }));
  };

  const movePhoto = (idx, delta) => {
    const next = [...form.photos];
    const target = idx + delta;
    if (target < 0 || target >= next.length) return;
    [next[idx], next[target]] = [next[target], next[idx]];
    setForm((f) => ({ ...f, photos: next }));
  };

  const submit = async (e) => {
    e.preventDefault();
    setSaving(true);
    setError("");
    const body = { ...form };
    ["bedrooms", "bathrooms", "sqft", "asking_price", "estimated_arv", "estimated_repairs"].forEach((k) => {
      body[k] = body[k] === "" || body[k] == null ? null : Number(body[k]);
    });
    try {
      if (initial) await api.patch(`/leads/${initial.id}`, body);
      else await api.post("/leads", body);
      onSaved();
    } catch (err) {
      setError(err?.response?.data?.detail || "Failed to save");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-end md:items-center justify-center bg-black/40 backdrop-blur-sm p-4" onClick={onClose} data-testid="lead-modal">
      <div
        className="bg-white w-full max-w-2xl rounded-2xl shadow-xl max-h-[90vh] overflow-y-auto"
        onClick={(e) => e.stopPropagation()}
        role="dialog"
        aria-modal="true"
        aria-labelledby="lead-modal-title"
      >
        <div className="flex items-center justify-between p-6 border-b border-[var(--line)] sticky top-0 bg-white z-10">
          <div>
            <div className="label-eyebrow">{initial ? "Edit lead" : "New lead"}</div>
            <h2 id="lead-modal-title" className="font-display font-bold text-xl mt-1">{initial ? initial.property_address : "Capture a new seller"}</h2>
          </div>
          <button type="button" className="btn-ghost" onClick={onClose} data-testid="lead-modal-close"><X size={16} /></button>
        </div>
        <form onSubmit={submit} className="p-6 grid md:grid-cols-2 gap-4" data-testid="lead-form">
          <div className="md:col-span-2">
            <label className="field-label flex items-center gap-1.5">
              <ImageIcon size={11} /> Property photos
              <span className="text-[10px] text-[var(--ink-3)] font-normal normal-case tracking-normal">
                — first photo becomes the report hero
              </span>
            </label>

            <div className="flex gap-2 mb-3">
              <input
                className="input flex-1"
                type="url"
                placeholder="Paste a Zillow/Redfin/MLS photo URL and press Enter"
                value={photoDraft}
                onChange={(e) => setPhotoDraft(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === "Enter") {
                    e.preventDefault();
                    addPhoto();
                  }
                }}
                data-testid="lead-photo-input"
              />
              <button
                type="button"
                className="btn-outline whitespace-nowrap"
                onClick={addPhoto}
                disabled={!photoDraft.trim()}
                data-testid="lead-photo-add"
              >
                <Plus size={14} /> Add
              </button>
            </div>

            {form.photos.length > 0 ? (
              <div className="grid grid-cols-3 sm:grid-cols-4 lg:grid-cols-6 gap-2" data-testid="lead-photo-gallery">
                {form.photos.map((url, idx) => (
                  <div key={url} className="relative group rounded-lg overflow-hidden border border-[var(--line)]" data-testid={`lead-photo-tile-${idx}`}>
                    <img
                      src={url}
                      alt={`Photo ${idx + 1}`}
                      className="w-full aspect-square object-cover"
                      onError={(e) => { e.target.src = "data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 100 100'%3E%3Crect fill='%23F1F5F9' width='100' height='100'/%3E%3Ctext x='50' y='55' text-anchor='middle' font-size='10' fill='%2394A3B8'%3Ebroken%20URL%3C/text%3E%3C/svg%3E"; }}
                    />
                    {idx === 0 && (
                      <span className="absolute top-1 left-1 bg-[var(--primary)] text-white text-[9px] font-bold px-1.5 py-0.5 rounded uppercase tracking-wide">
                        Hero
                      </span>
                    )}
                    <div className="absolute inset-0 bg-black/0 group-hover:bg-black/60 transition-colors flex items-center justify-center gap-1 opacity-0 group-hover:opacity-100">
                      <button
                        type="button"
                        onClick={() => movePhoto(idx, -1)}
                        disabled={idx === 0}
                        className="w-7 h-7 rounded bg-white/90 text-[var(--ink)] flex items-center justify-center disabled:opacity-30"
                        title="Move left"
                        data-testid={`lead-photo-up-${idx}`}
                      >
                        <ChevronUp size={12} />
                      </button>
                      <button
                        type="button"
                        onClick={() => movePhoto(idx, 1)}
                        disabled={idx === form.photos.length - 1}
                        className="w-7 h-7 rounded bg-white/90 text-[var(--ink)] flex items-center justify-center disabled:opacity-30"
                        title="Move right"
                        data-testid={`lead-photo-down-${idx}`}
                      >
                        <ChevronDown size={12} />
                      </button>
                      <button
                        type="button"
                        onClick={() => removePhoto(idx)}
                        className="w-7 h-7 rounded bg-rose-500 text-white flex items-center justify-center"
                        title="Remove"
                        data-testid={`lead-photo-remove-${idx}`}
                      >
                        <X size={12} />
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <div className="border-2 border-dashed border-slate-300 rounded-lg p-6 text-center text-sm text-[var(--ink-3)]" data-testid="lead-photo-empty">
                <ImageIcon size={20} className="mx-auto mb-1.5 text-[var(--ink-3)]" />
                No photos yet. Add up to 12 — the first one becomes the report hero.
              </div>
            )}
          </div>
          <div className="md:col-span-2">
            <label className="field-label">Property address *</label>
            <AddressAutocomplete
              name="property_address"
              placeholder="Start typing — we'll suggest verified addresses"
              inputClassName="input"
              dataTestid="lead-property-address"
              value={form.property_address}
              onChange={(v) => setForm({ ...form, property_address: v })}
              onSelect={(label) => setForm({ ...form, property_address: label })}
            />
            {showDupes && (
              <div
                className="mt-2 rounded-lg border border-amber-300 bg-amber-50 p-3 text-sm"
                data-testid="lead-dupe-warning"
              >
                <div className="flex items-start justify-between gap-3">
                  <div className="flex items-start gap-2 text-amber-900">
                    <AlertTriangle size={16} className="mt-0.5 flex-shrink-0" />
                    <div>
                      <div className="font-semibold">
                        {dupes.length === 1
                          ? "We found a possible duplicate in your CRM."
                          : `We found ${dupes.length} possible duplicates in your CRM.`}
                      </div>
                      <div className="text-xs text-amber-800 mt-0.5">
                        Click a match to open it instead of creating a second copy.
                      </div>
                    </div>
                  </div>
                  <button
                    type="button"
                    onClick={() => setDupeDismissed(true)}
                    className="text-amber-700 hover:text-amber-900 text-xs underline whitespace-nowrap"
                    data-testid="lead-dupe-dismiss"
                  >
                    Ignore
                  </button>
                </div>
                <ul className="mt-2 space-y-1.5">
                  {dupes.map((d) => (
                    <li
                      key={d.id}
                      className="rounded-md border border-amber-200 bg-white overflow-hidden"
                    >
                      <div className="flex items-stretch">
                        <button
                          type="button"
                          onClick={() => { onClose(); nav(`/v3/properties/${d.id}`); }}
                          className="flex-1 text-left px-2.5 py-1.5 hover:bg-amber-100 transition"
                          data-testid={`lead-dupe-match-${d.id}`}
                          disabled={mergingId === d.id}
                        >
                          <div className="flex items-center justify-between gap-2">
                            <div className="min-w-0 flex-1">
                              <div className="font-medium text-[var(--ink)] truncate">{d.property_address}</div>
                              <div className="text-xs text-[var(--ink-3)] truncate">
                                {[d.city, d.state, d.zip_code].filter(Boolean).join(", ")}
                                {d.stage ? ` · ${d.stage}` : ""}
                                {d.asking_price ? ` · ${fmtMoney(d.asking_price)}` : ""}
                              </div>
                            </div>
                            <span
                              className={`text-[10px] font-bold uppercase tracking-wide px-1.5 py-0.5 rounded ${
                                d.confidence === "exact"
                                  ? "bg-rose-100 text-rose-700"
                                  : d.confidence === "very-likely"
                                  ? "bg-amber-200 text-amber-900"
                                  : "bg-slate-100 text-slate-700"
                              }`}
                            >
                              {d.confidence.replace("-", " ")} · {Math.round(d.score * 100)}%
                            </span>
                          </div>
                        </button>
                        <button
                          type="button"
                          onClick={() => handleMerge(d.id)}
                          disabled={mergingId !== null}
                          className="px-2.5 border-l border-amber-200 text-xs font-semibold text-amber-900 hover:bg-amber-200 transition disabled:opacity-50 disabled:cursor-wait whitespace-nowrap"
                          title="Merge this form's data into the existing lead (fills blanks, appends notes, unions photos — never overwrites real data)"
                          data-testid={`lead-dupe-merge-${d.id}`}
                        >
                          {mergingId === d.id ? "Merging…" : "Merge"}
                        </button>
                      </div>
                    </li>
                  ))}
                </ul>
              </div>
            )}
            {!isEdit && dupeChecking && dupes.length === 0 && (
              <div className="mt-1.5 text-xs text-[var(--ink-3)]" data-testid="lead-dupe-checking">
                Checking for duplicates…
              </div>
            )}
          </div>
          <div><label className="field-label">City</label><input className="input" value={form.city} onChange={set("city")} data-testid="lead-city" /></div>
          <div className="grid grid-cols-2 gap-3">
            <div><label className="field-label">State</label><input className="input" value={form.state} onChange={set("state")} data-testid="lead-state" /></div>
            <div><label className="field-label">ZIP</label><input className="input" value={form.zip_code} onChange={set("zip_code")} data-testid="lead-zip" /></div>
          </div>

          <div className="md:col-span-2 grid grid-cols-3 gap-3">
            <div><label className="field-label">Beds</label><input className="input" type="number" min="0" value={form.bedrooms} onChange={set("bedrooms")} data-testid="lead-bedrooms" /></div>
            <div><label className="field-label">Baths</label><input className="input" type="number" min="0" step="0.5" value={form.bathrooms} onChange={set("bathrooms")} data-testid="lead-bathrooms" /></div>
            <div><label className="field-label">SqFt</label><input className="input" type="number" min="0" value={form.sqft} onChange={set("sqft")} data-testid="lead-sqft" /></div>
          </div>

          <div><label className="field-label">Asking price</label><input className="input" type="number" min="0" value={form.asking_price} onChange={set("asking_price")} data-testid="lead-asking" /></div>
          <div><label className="field-label">Estimated ARV</label><input className="input" type="number" min="0" value={form.estimated_arv} onChange={set("estimated_arv")} data-testid="lead-arv" /></div>
          <div className="md:col-span-2"><label className="field-label">Estimated repairs</label><input className="input" type="number" min="0" value={form.estimated_repairs} onChange={set("estimated_repairs")} data-testid="lead-repairs" /></div>

          <div className="md:col-span-2 divider my-2" />

          <div><label className="field-label">Seller name</label><input className="input" value={form.seller_name} onChange={set("seller_name")} data-testid="lead-seller-name" /></div>
          <div><label className="field-label">Phone</label><input className="input" value={form.seller_phone} onChange={set("seller_phone")} data-testid="lead-seller-phone" /></div>
          <div className="md:col-span-2"><label className="field-label">Email</label><input className="input" type="email" value={form.seller_email} onChange={set("seller_email")} data-testid="lead-seller-email" /></div>

          <div>
            <label className="field-label">Source</label>
            <select className="select" value={form.lead_source} onChange={set("lead_source")} data-testid="lead-source">
              {SOURCES.map((s) => <option key={s}>{s}</option>)}
            </select>
          </div>
          <div>
            <label className="field-label">Stage</label>
            <select className="select" value={form.stage} onChange={set("stage")} data-testid="lead-stage">
              {STAGES.map((s) => <option key={s}>{s}</option>)}
            </select>
          </div>
          <div className="md:col-span-2">
            <label className="field-label">Notes</label>
            <textarea className="textarea" rows={4} value={form.notes} onChange={set("notes")} data-testid="lead-notes" />
          </div>

          <BuyBoxPreviewPanel formData={form} />

          {error && <div className="md:col-span-2 banner-err" data-testid="lead-error">{String(error)}</div>}
          <div className="md:col-span-2 flex justify-end gap-3 pt-3 border-t border-[var(--line)]">
            <button type="button" className="btn-outline" onClick={onClose}>Cancel</button>
            <button type="submit" className="btn-money" disabled={saving} data-testid="lead-save-button">
              {saving ? "Saving…" : initial ? "Save changes" : "Create lead"}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

// ──────────────────────────────────────────────────────────────────────────
// List-Stacking Pyramid (Pro+) — shows how many addresses appear on N+ lists.
// Click a bucket or a list-chip to filter the table below.
// ──────────────────────────────────────────────────────────────────────────
function StackPyramidCard({ summary, stackFilter, setStackFilter, listFilter, setListFilter }) {
  const buckets = summary.stack_buckets || [];
  const sources = summary.sources || [];
  const one = buckets.find((b) => b.list_count === 1)?.leads || 0;
  const two = buckets.find((b) => b.list_count === 2)?.leads || 0;
  const threePlus = buckets.filter((b) => b.list_count >= 3).reduce((a, b) => a + b.leads, 0);
  const total = one + two + threePlus;
  if (total === 0 && sources.length === 0) return null;

  const Cell = ({ value, label, k, tone, hot }) => {
    const isActive = stackFilter === k;
    const palette = {
      slate: "bg-slate-100 text-slate-800 hover:bg-slate-200 border-slate-200",
      amber: "bg-amber-50 text-amber-900 hover:bg-amber-100 border-amber-200",
      rose: "bg-rose-50 text-rose-900 hover:bg-rose-100 border-rose-300",
    }[tone];
    const ringClass = isActive ? "ring-2 ring-offset-1 ring-indigo-500" : "";
    return (
      <button
        type="button"
        onClick={() => setStackFilter(isActive ? "all" : k)}
        className={`relative flex-1 min-w-0 rounded-lg border ${palette} ${ringClass} transition-all px-4 py-3 text-left`}
        data-testid={`stack-bucket-${k}`}
        aria-pressed={isActive}
      >
        <div className="font-display font-extrabold text-2xl tabular-nums">{value.toLocaleString()}</div>
        <div className="text-[11px] font-bold uppercase tracking-wide opacity-80">{label}</div>
        {hot && (
          <span className="absolute top-2 right-2 text-[9px] font-bold uppercase tracking-wider px-1.5 py-0.5 rounded bg-rose-600 text-white">
            🔥 hot
          </span>
        )}
      </button>
    );
  };

  return (
    <div className="card mb-5 p-4" data-testid="stack-pyramid-card">
      <div className="flex items-start justify-between gap-3 mb-3">
        <div className="min-w-0">
          <h3 className="font-display font-extrabold text-lg flex items-center gap-2">
            List Stacking
            <span className="px-1.5 py-0.5 rounded bg-emerald-100 text-emerald-700 text-[9px] font-bold uppercase tracking-wider">
              Pro
            </span>
          </h3>
          <p className="text-xs text-[var(--ink-2)] mt-0.5">
            Addresses appearing on multiple lists convert at <strong>3–5×</strong> baseline.
            Click a tier to filter the table below.
          </p>
        </div>
        {(stackFilter !== "all" || listFilter) && (
          <button
            type="button"
            onClick={() => { setStackFilter("all"); setListFilter(""); }}
            className="text-[11px] font-semibold text-indigo-600 hover:underline"
            data-testid="stack-clear-filters"
          >
            Clear filters
          </button>
        )}
      </div>
      <div className="flex items-stretch gap-2">
        <Cell value={one} label="On 1 list" k="1" tone="slate" />
        <Cell value={two} label="On 2 lists" k="2" tone="amber" />
        <Cell value={threePlus} label="On 3+ lists" k="3plus" tone="rose" hot />
      </div>
      {sources.length > 0 && (
        <div className="mt-3 pt-3 border-t border-[var(--line)]">
          <div className="text-[11px] font-bold uppercase tracking-wide text-[var(--ink-3)] mb-2">
            Sources ({sources.length})
          </div>
          <div className="flex flex-wrap gap-1.5">
            {sources.map((s) => {
              const active = listFilter === s.name;
              return (
                <button
                  type="button"
                  key={s.name}
                  onClick={() => setListFilter(active ? "" : s.name)}
                  className={`px-2.5 py-1 rounded-full text-[11px] font-semibold border transition-colors ${
                    active
                      ? "bg-indigo-600 text-white border-indigo-600"
                      : "bg-white text-[var(--ink)] border-[var(--line)] hover:bg-slate-50"
                  }`}
                  data-testid={`list-chip-${s.name.replace(/\s+/g, "-").toLowerCase()}`}
                  aria-pressed={active}
                >
                  {s.name}
                  <span className="ml-1.5 opacity-70 tabular-nums">{s.leads}</span>
                </button>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
}

